import React, { useCallback, useEffect, useState } from "react";
import { Globe, ShieldAlert, Trash2, Database, Flame, Mountain, CandlestickChart } from "lucide-react";
import { toast } from "sonner";
import {
    fetchSgBinance,
    fetchSgCrossx,
    fetchSgRecorder,
    fetchSgGuards,
    updateSgGuards,
    resetSgPaper,
    fetchSgMacro,
} from "@/lib/smartgrid_api";

const C = {
    bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B",
    bear: "#FF5C6C", warn: "#FFB020", info: "#3DA7FF",
};

const Head = ({ icon: Icon, title, right }) => (
    <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
        <Icon size={14} color={C.accent} />
        <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>{title}</span>
        {right && <span className="ml-auto flex items-center gap-2">{right}</span>}
    </div>
);

const Pill = ({ ok, onText, offText }) => (
    <span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono inline-flex items-center gap-1"
        style={{ color: ok ? C.bull : C.bear, background: "#13202D" }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: ok ? C.bull : C.bear }} />
        {ok ? onText : offText}
    </span>
);

const Stat = ({ label, value, color = C.text }) => (
    <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: "#13202D" }}>
        <span className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>{label}</span>
        <span className="font-mono text-sm" style={{ color }}>{value}</span>
    </div>
);

const Toggle = ({ on, onClick, disabled }) => (
    <button type="button" onClick={onClick} disabled={disabled}
        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-40"
        style={{ background: on ? C.accent : "#26364A" }} data-state={on ? "on" : "off"}>
        <span className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
            style={{ transform: on ? "translateX(18px)" : "translateX(2px)" }} />
    </button>
);

const ago = (ts) => {
    if (!ts) return "—";
    const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    return `${Math.round(s / 3600)}h ago`;
};

const fmtLastMove = (m) => {
    if (!m) return "none yet";
    const dir = m.direction > 0 ? "▲ up" : "▼ down";
    const mag = m.reason === "liq_burst"
        ? `$${Number(m.magnitude ?? m.usd ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 })} liquidated`
        : `${Number(m.magnitude ?? m.move_bps ?? 0).toFixed(1)} bps`;
    return `${dir} · ${mag} · ${ago(m.ts_ms)}`;
};

// ============ Binance leader-venue feed + cross-exchange intel ============
export const BinancePanel = () => {
    const [bn, setBn] = useState(null);
    const [xx, setXx] = useState(null);
    const [rec, setRec] = useState(null);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [b, x, r] = await Promise.all([fetchSgBinance(), fetchSgCrossx(), fetchSgRecorder()]);
                if (!alive) return;
                setBn(b); setXx(x); setRec(r);
            } catch (_) { /* transient */ }
        };
        tick();
        const id = setInterval(tick, 3000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const spot = bn?.spot || {};
    const fut = bn?.futures || {};
    const ws = bn?.ws || {};
    const f = xx?.features || {};
    const basis = f.xex_basis_bps;
    const z = f.xex_basis_z;
    const zColor = z == null ? C.dim : z >= 1.5 ? C.bear : z <= -1.5 ? C.bull : C.text;

    return (
        <div className="panel" data-testid="sg-binance-panel">
            <Head icon={Globe} title="Binance Leader Feed"
                right={<>
                    <Pill ok={!!ws?.spot?.is_connected} onText="spot ws" offText="spot down" />
                    <Pill ok={!!ws?.futures?.is_connected} onText="fut ws" offText="fut down" />
                    {fut.source === "bybit" && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono"
                            style={{ color: C.warn, background: "#13202D" }}
                            title="Binance futures is connected but silent (region-blocked) — mark/funding/OI/liquidations are served by the keyless Bybit fallback">
                            fut: bybit fallback
                        </span>
                    )}
                </>} />
            <div className="p-4">
                {!bn?.available && (
                    <div className="text-[11px] pb-2" style={{ color: C.dim }}>
                        Feed not initialised — cross-exchange features are omitted (never mocked).
                    </div>
                )}
                <Stat label="Binance Mid" value={spot.mid ? `$${Number(spot.mid).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "—"} />
                <Stat label="AsterDex Basis" value={basis != null ? `${Number(basis).toFixed(2)} bps` : "—"}
                    color={basis == null ? C.dim : basis >= 0 ? C.warn : C.info} />
                <Stat label="Basis Z-score" value={z != null ? Number(z).toFixed(2) : "warming up"} color={zColor} />
                <Stat label="Lead Momentum 5s" value={f.xex_bn_mom_5s_bps != null ? `${Number(f.xex_bn_mom_5s_bps).toFixed(2)} bps` : "—"}
                    color={(f.xex_bn_mom_5s_bps || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="L2 Imbalance (top5)" value={f.xex_bn_depth_imb_5 != null ? Number(f.xex_bn_depth_imb_5).toFixed(3) : "—"}
                    color={(f.xex_bn_depth_imb_5 || 0) >= 0 ? C.bull : C.bear} />
                <Stat label="Perp Mark"
                    value={fut.mark_price
                        ? `$${Number(fut.mark_price).toLocaleString("en-US", { maximumFractionDigits: 2 })}${fut.data_age_ms != null ? ` · ${ago(Date.now() - fut.data_age_ms)}` : ""}`
                        : (ws?.futures?.is_connected ? "connected, no data yet" : "fut ws down")}
                    color={fut.stale ? C.warn : C.text} />
                <Stat label="Funding Rate"
                    value={fut.funding_rate != null
                        ? `${(fut.funding_rate * 100).toFixed(4)}% (8h)${fut.next_funding_ms ? ` · next in ${Math.max(0, Math.round((fut.next_funding_ms - Date.now()) / 60000))}m` : ""}`
                        : "—"}
                    color={(fut.funding_rate || 0) >= 0 ? C.bull : C.bear} />
                {fut.stale && fut.source !== "bybit" && (
                    <div className="text-[10px] py-1" style={{ color: C.warn }}>
                        ⚠ futures socket connected but silent {Math.round((fut.data_age_ms || 0) / 1000)}s — likely region-blocked (fstream.binance.com); the Bybit fallback will take over automatically
                    </div>
                )}
                {fut.source === "bybit" && (
                    <div className="text-[10px] py-1" style={{ color: C.sub }}>
                        Futures data via Bybit fallback (Binance futures region-blocked). Taker ratio = Bybit account long/short (closest free proxy).
                    </div>
                )}
                <Stat label="Open Interest" value={fut.open_interest?.value
                    ? `${Number(fut.open_interest.value).toLocaleString("en-US", { maximumFractionDigits: 0 })} (${fut.open_interest.change_pct ?? 0}%)`
                    : "—"} />
                <Stat label="Taker Buy/Sell" value={fut.taker_ratio?.buy_sell_ratio != null ? Number(fut.taker_ratio.buy_sell_ratio).toFixed(3) : "—"}
                    color={(fut.taker_ratio?.buy_sell_ratio || 1) >= 1 ? C.bull : C.bear} />
                <Stat label="Liqs (5m)" value={fut.liq_count_5m ?? 0} color={fut.liq_count_5m > 0 ? C.warn : C.text} />
                <Stat label="Last Fast Move" value={fmtLastMove(xx?.last_fast_move || bn?.fast_move?.last)} color={C.sub} />
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono"
                        style={{ color: xx?.guard_active ? C.bear : C.dim, background: "#13202D" }}>
                        {xx?.guard_active ? "⛔ stale-quote guard ACTIVE" : "stale-quote guard idle"}
                    </span>
                    <span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono"
                        style={{ color: xx?.pump_guard_active ? C.warn : C.dim, background: "#13202D" }}>
                        {xx?.pump_guard_active ? "🚀 pump guard ACTIVE" : "pump guard idle"}
                    </span>
                </div>
                <div className="mt-2 flex items-center gap-1.5 text-[10px]" style={{ color: C.dim }}>
                    <Database size={11} />
                    recorder {rec?.enabled ? `on · ${Number(rec.rows_recorded || 0).toLocaleString("en-US")} rows · ${Number(rec.docs_written || 0).toLocaleString("en-US")} docs` : "off"}
                    {rec?.write_errors > 0 && <span style={{ color: C.warn }}> · {rec.write_errors} write errors</span>}
                </div>
                <div className="mt-1 text-[10px]" style={{ color: C.dim }}>
                    ws msgs — spot {Number(ws?.spot?.messages_received || 0).toLocaleString("en-US")} · futures {Number(ws?.futures?.messages_received || 0).toLocaleString("en-US")}
                    {(ws?.futures?.disconnect_count || 0) > 2 && <span style={{ color: C.warn }}> · {ws.futures.disconnect_count} fut reconnects</span>}
                </div>
            </div>
        </div>
    );
};

// ============ Macro trend (1y klines) + candlestick patterns ============
const MACRO_COLOR = (r) => {
    if (!r) return C.dim;
    if (r.includes("Bull")) return C.bull;
    if (r.includes("Bear")) return C.bear;
    if (r === "Correction") return C.warn;
    return C.sub;
};

const PATTERN_LABEL = (n) => (n || "").replace(/_/g, " ");

export const MacroPanel = () => {
    const [m, setM] = useState(null);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const d = await fetchSgMacro();
                if (alive) setM(d);
            } catch (_) { /* transient */ }
        };
        tick();
        const id = setInterval(tick, 30_000);   // macro changes on candle closes
        return () => { alive = false; clearInterval(id); };
    }, []);

    const met = m?.metrics || {};
    const bias = m?.pattern_bias || {};
    const recent = [];
    for (const tf of ["1d", "4h", "1h", "1w"]) {
        for (const p of (m?.patterns?.[tf] || [])) {
            recent.push({ ...p, tf });
        }
    }
    recent.sort((a, b) => (b.ts_ms || 0) - (a.ts_ms || 0));

    return (
        <div className="panel" data-testid="sg-macro-panel">
            <Head icon={Mountain} title="Macro Trend — 1 Year View"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-[0.1em]"
                    style={{ color: MACRO_COLOR(m?.regime), background: "#13202D" }}
                    data-testid="sg-macro-regime">
                    {m?.regime || "loading"}
                </span>} />
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <div>
                    <Stat label="Macro Score" value={m?.macro_trend_score != null ? Number(m.macro_trend_score).toFixed(2) : "—"}
                        color={(m?.macro_trend_score || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="vs SMA 200d" value={met.dist_sma200_pct != null ? `${met.dist_sma200_pct > 0 ? "+" : ""}${met.dist_sma200_pct}%` : "—"}
                        color={(met.dist_sma200_pct || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="vs SMA 50d" value={met.dist_sma50_pct != null ? `${met.dist_sma50_pct > 0 ? "+" : ""}${met.dist_sma50_pct}%` : "—"}
                        color={(met.dist_sma50_pct || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="MA Cross" value={met.cross ? met.cross.toUpperCase() : "—"}
                        color={met.cross === "golden" ? C.bull : met.cross === "death" ? C.bear : C.dim} />
                    <Stat label="1y Drawdown" value={met.drawdown_1y_pct != null ? `${met.drawdown_1y_pct}%` : "—"} color={C.bear} />
                    <Stat label="Vol 30d pctile" value={met.vol_30d_pctile != null ? `${Math.round(met.vol_30d_pctile * 100)}th` : "—"} color={C.warn} />
                </div>
                <div>
                    <Stat label="Return 30d" value={met.ret_30d != null ? `${met.ret_30d}%` : "—"} color={(met.ret_30d || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="Return 90d" value={met.ret_90d != null ? `${met.ret_90d}%` : "—"} color={(met.ret_90d || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="Return 1y" value={met.ret_365d != null ? `${met.ret_365d}%` : "—"} color={(met.ret_365d || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="8w Slope" value={met.slope_8w_pct_per_wk != null ? `${met.slope_8w_pct_per_wk}%/wk` : "—"}
                        color={(met.slope_8w_pct_per_wk || 0) >= 0 ? C.bull : C.bear} />
                    <Stat label="Pattern Bias 1h/4h/1d"
                        value={["1h", "4h", "1d"].map((tf) => (bias[tf] != null ? Number(bias[tf]).toFixed(2) : "·")).join(" / ")}
                        color={C.sub} />
                    <Stat label="Structure" value={met.swing_structure === 1 ? "HH+HL" : met.swing_structure === -1 ? "LH+LL" : "mixed"}
                        color={met.swing_structure === 1 ? C.bull : met.swing_structure === -1 ? C.bear : C.dim} />
                </div>
            </div>
            <div className="px-4 pb-4">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] pb-1.5" style={{ color: C.dim }}>
                    <CandlestickChart size={12} /> Recent candlestick patterns
                </div>
                <div className="space-y-1 max-h-[150px] overflow-y-auto" data-testid="sg-macro-patterns">
                    {recent.length === 0 && (
                        <div className="text-[11px]" style={{ color: "#3A4759" }}>
                            {m?.available ? "No qualified patterns on recent closed candles." : "Backfilling 1 year of Binance klines…"}
                        </div>
                    )}
                    {recent.slice(0, 10).map((p, i) => (
                        <div key={`${p.tf}-${p.ts_ms}-${p.name}-${i}`} className="flex items-center gap-2 text-[11px] rounded px-2 py-1"
                            style={{ background: "#0B1219" }}>
                            <span className="font-mono text-[9px] px-1 rounded" style={{ color: C.accent, background: "#13202D" }}>{p.tf}</span>
                            <span className="font-mono" style={{ color: p.direction > 0 ? C.bull : p.direction < 0 ? C.bear : C.sub }}>
                                {p.direction > 0 ? "▲" : p.direction < 0 ? "▼" : "◆"} {PATTERN_LABEL(p.name)}
                            </span>
                            <span className="ml-auto" style={{ color: C.dim }}>{p.age != null ? `${p.age} bars ago` : ago(p.ts_ms)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============ Guards: fast-move + liquidation-burst protection ============
export const GuardsPanel = () => {
    const [g, setG] = useState(null);
    const [saving, setSaving] = useState(false);
    const [bps, setBps] = useState("");
    const [pump, setPump] = useState("");
    const [usd, setUsd] = useState("");
    const [editing, setEditing] = useState(false);

    const load = useCallback(async () => {
        try {
            const d = await fetchSgGuards();
            setG(d);
            if (!editing) {
                setBps(d?.fastmove_bps != null ? String(d.fastmove_bps) : "");
                setPump(d?.pump_bps != null ? String(d.pump_bps) : "");
                setUsd(d?.liq_burst_usd != null ? String(d.liq_burst_usd) : "");
            }
        } catch (_) { /* transient */ }
    }, [editing]);

    useEffect(() => { load(); const id = setInterval(load, 4000); return () => clearInterval(id); }, [load]);

    const post = async (body, okMsg) => {
        setSaving(true);
        try {
            const r = await updateSgGuards(body);
            setG(r);
            setBps(r?.fastmove_bps != null ? String(r.fastmove_bps) : "");
            setPump(r?.pump_bps != null ? String(r.pump_bps) : "");
            setUsd(r?.liq_burst_usd != null ? String(r.liq_burst_usd) : "");
            setEditing(false);
            toast.success(okMsg);
        } catch (e) {
            toast.error(`Guard update failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const auto = !!g?.auto_tune;
    const cal = g?.calibration || {};
    const cnt = g?.counters || {};
    const bounds = g?.bounds || {};
    const inputCls = "bg-[#0B0F14] border rounded px-2 py-1 text-[12px] font-mono w-28 disabled:opacity-40";

    return (
        <div className="panel" data-testid="sg-guards-panel">
            <Head icon={ShieldAlert} title="Leader-Venue Guards"
                right={<>
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>auto-tune</span>
                    <Toggle on={auto} disabled={saving}
                        onClick={() => post({ auto_tune: !auto },
                            !auto ? "Auto-tune ON — thresholds recalibrate from market data every ~10 min"
                                  : "Auto-tune OFF — thresholds are pinned manually")} />
                </>} />
            <div className="p-4">
                <div className="text-[11px] leading-snug pb-2.5" style={{ color: C.dim }}>
                    Fast Binance moves pause BUYs + pull the nearest rungs before arbitrageurs hit stale quotes.
                    {auto ? " Auto-tune sets thresholds at the tail of the observed distributions (turn it off to edit manually)."
                          : " Manual mode — thresholds are pinned to the values below."}
                </div>
                <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#13202D" }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Fast-move threshold</div>
                        <div className="text-[10px]" style={{ color: "#3A4759" }}>
                            bps in 800ms · bounds {bounds.fastmove_bps?.[0]}–{bounds.fastmove_bps?.[1]}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input type="number" step="0.5" value={bps} disabled={auto || saving}
                            onChange={(e) => { setBps(e.target.value); setEditing(true); }}
                            className={inputCls} style={{ borderColor: C.border, color: C.text }}
                            data-testid="sg-guard-bps-input" />
                        <span className="text-[10px] font-mono" style={{ color: C.dim }}>bps</span>
                    </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#13202D" }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Pump threshold</div>
                        <div className="text-[10px]" style={{ color: "#3A4759" }}>
                            up-move bps (suspends TP-trailing; bounce-filtered) · bounds {g?.bounds?.pump_bps?.[0]}–{g?.bounds?.pump_bps?.[1]}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input type="number" step="0.5" value={pump} disabled={auto || saving}
                            onChange={(e) => { setPump(e.target.value); setEditing(true); }}
                            className={inputCls} style={{ borderColor: C.border, color: C.text }}
                            data-testid="sg-guard-pump-input" />
                        <span className="text-[10px] font-mono" style={{ color: C.dim }}>bps</span>
                    </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#13202D" }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>Liq-burst threshold</div>
                        <div className="text-[10px]" style={{ color: "#3A4759" }}>
                            USD liquidated in 30s · bounds ${Number(bounds.liq_burst_usd?.[0] || 0) / 1000}k–${Number(bounds.liq_burst_usd?.[1] || 0) / 1e6}M
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input type="number" step="10000" value={usd} disabled={auto || saving}
                            onChange={(e) => { setUsd(e.target.value); setEditing(true); }}
                            className={inputCls} style={{ borderColor: C.border, color: C.text }}
                            data-testid="sg-guard-usd-input" />
                        <span className="text-[10px] font-mono" style={{ color: C.dim }}>USD</span>
                    </div>
                </div>
                {!auto && (
                    <button
                        onClick={() => post({ fastmove_bps: Number(bps), pump_bps: Number(pump || bps * 1.5), liq_burst_usd: Number(usd) },
                            "Guard thresholds pinned")}
                        disabled={saving || !bps || !usd}
                        className="btn-anim mt-2.5 h-8 px-3 rounded-md text-[11px] font-semibold disabled:opacity-40"
                        style={{ background: C.accent, color: C.bg }}
                        data-testid="sg-guards-save">
                        Apply manual thresholds
                    </button>
                )}
                <div className="mt-3 grid grid-cols-2 gap-x-4">
                    <Stat label="Fast moves" value={cnt.fast_moves ?? 0} color={cnt.fast_moves > 0 ? C.warn : C.text} />
                    <Stat label="Liq bursts" value={cnt.liq_bursts ?? 0} color={cnt.liq_bursts > 0 ? C.warn : C.text} />
                    <Stat label="Slides (CUSUM)" value={cnt.slides ?? 0} color={cnt.slides > 0 ? C.warn : C.text} />
                    <Stat label="Move samples" value={`${cal.move_samples ?? 0} / 600`} color={C.sub} />
                    <Stat label="Liq samples" value={`${cal.liq_samples ?? 0} / 30`} color={C.sub} />
                </div>
                <div className="mt-2 text-[10px] flex items-center gap-1.5" style={{ color: C.dim }}>
                    <Flame size={11} />
                    {cal.last_tune_ms
                        ? `last auto-tune ${ago(cal.last_tune_ms)}`
                        : auto ? "no auto-tune yet — collecting calibration data" : "auto-tune disabled"}
                </div>
            </div>
        </div>
    );
};

// ============ Danger zone: PAPER-ONLY full reset ============
export const PaperResetCard = ({ botStatus, onDone }) => {
    const [open, setOpen] = useState(false);
    const [confirm, setConfirm] = useState("");
    const [wipeLearning, setWipeLearning] = useState(true);
    const [busy, setBusy] = useState(false);
    const isPaper = (botStatus?.mode || "paper") === "paper";

    const doReset = async () => {
        setBusy(true);
        try {
            const r = await resetSgPaper(wipeLearning);
            if (r?.ok) {
                toast.success("SmartGrid paper data reset", {
                    description: `wiped: ${(r.wiped || []).join(", ")} · learning ${r.learning_reset ? "reset" : "kept"} · bot left stopped`,
                });
                setOpen(false); setConfirm("");
                onDone && onDone();
            } else {
                toast.error(r?.error || "Reset refused");
            }
        } catch (e) {
            toast.error(`Reset failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="panel" style={{ borderColor: "rgba(255,92,108,0.35)" }} data-testid="sg-paper-reset-card">
            <Head icon={Trash2} title="Danger Zone — Paper Reset"
                right={<span className="text-[9px] uppercase px-1.5 py-0.5 rounded font-mono"
                    style={{ color: isPaper ? C.bull : C.bear, background: "#13202D" }}>
                    mode: {botStatus?.mode || "—"}
                </span>} />
            <div className="p-4">
                <div className="text-[11px] leading-snug" style={{ color: C.dim }}>
                    Wipes ALL SmartGrid bot data: orders, trades, PnL, cycles, paper wallet
                    {wipeLearning ? ", and every learned AI component (twins, bandit, meta-controller, online ML)" : ""}.
                    Recorded market data is kept. <span style={{ color: C.bear }}>PAPER MODE ONLY</span> — the
                    backend refuses in live. The bot is left stopped.
                </div>
                {!open && (
                    <button
                        onClick={() => setOpen(true)}
                        disabled={!isPaper}
                        className="btn-anim mt-3 h-8 px-3 rounded-md text-[11px] font-semibold disabled:opacity-40"
                        style={{ background: "rgba(255,92,108,0.15)", color: C.bear, border: `1px solid ${C.bear}50` }}
                        data-testid="sg-paper-reset-open">
                        {isPaper ? "Reset all paper data…" : "Switch to PAPER to enable reset"}
                    </button>
                )}
                {open && (
                    <div className="mt-3 rounded-md p-3 space-y-2.5" style={{ background: "#0B1219", border: `1px solid ${C.bear}40` }}>
                        <label className="flex items-center gap-2 text-[11px]" style={{ color: C.sub }}>
                            <input type="checkbox" checked={wipeLearning}
                                onChange={(e) => setWipeLearning(e.target.checked)} />
                            Also reset AI learning (twins, bandit, meta, online ML)
                        </label>
                        <div className="flex items-center gap-2">
                            <input
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                placeholder='type "RESET"'
                                className="bg-[#0B0F14] border rounded px-2 py-1.5 text-[12px] font-mono w-32"
                                style={{ borderColor: C.bear, color: C.text }}
                                data-testid="sg-paper-reset-confirm-input"
                            />
                            <button
                                onClick={doReset}
                                disabled={busy || confirm.trim().toUpperCase() !== "RESET"}
                                className="btn-anim h-8 px-3 rounded-md text-[11px] font-semibold disabled:opacity-40"
                                style={{ background: C.bear, color: "#fff" }}
                                data-testid="sg-paper-reset-confirm">
                                {busy ? "Resetting…" : "Confirm reset"}
                            </button>
                            <button onClick={() => { setOpen(false); setConfirm(""); }}
                                className="btn-anim h-8 px-3 rounded-md text-[11px]"
                                style={{ color: C.sub, border: `1px solid ${C.border}` }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BinancePanel;
